import axios from 'axios';

const API_BASE = process.env.REACT_APP_API_URL || 'http://localhost:8001';

// Create axios instance with auth interceptor
const apiClient = axios.create({
  baseURL: API_BASE,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Add auth token to requests
apiClient.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('access_token');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Handle 401 errors
apiClient.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401) {
      const isLoginPage = window.location.pathname === '/login';
      if (!isLoginPage) {
        localStorage.removeItem('access_token');
        localStorage.removeItem('user');
        window.location.href = '/login';
      }
    }
    return Promise.reject(error);
  }
);

class MCPMasterService {
  // Servers
  async getGlobalServers(params) {
    const response = await apiClient.get('/api/v1/master/mcp/servers', { params });
    return response.data;
  }
  
  async createServer(data) {
    const response = await apiClient.post('/api/v1/master/mcp/servers', data);
    return response.data;
  }
  
  async updateServer(id, data) {
    const response = await apiClient.put(`/api/v1/master/mcp/servers/${id}`, data);
    return response.data;
  }
  
  async deleteServer(id) {
    const response = await apiClient.delete(`/api/v1/master/mcp/servers/${id}`);
    return response.data;
  }
  
  async testConnection(id) {
    const response = await apiClient.post(`/api/v1/master/mcp/servers/${id}/test`);
    return response.data;
  }
  
  async syncTools(id) {
    const response = await apiClient.post(`/api/v1/master/mcp/servers/${id}/sync-tools`);
    return response.data;
  }
  
  async getServerTools(id) {
    const response = await apiClient.get(`/api/v1/master/mcp/servers/${id}/tools`);
    return response.data;
  }
  
  async toggleServer(id, isActive) {
    const response = await apiClient.patch(`/api/v1/master/mcp/servers/${id}/toggle`, {
      is_active: isActive
    });
    return response.data;
  }
  
  async testConnectionConfig(type, connectionConfig) {
    const response = await apiClient.post('/api/v1/master/mcp/test-config', {
      type,
      connection_config: connectionConfig
    });
    return response.data;
  }
  
  // Templates
  async getTemplates() {
    try {
      const response = await apiClient.get('/api/v1/master/mcp/templates');
      const templates = response.data.templates || [];
      
      // Đảm bảo mỗi template có đầy đủ thông tin và config
      const processedTemplates = templates.map(template => {
        // Đảm bảo các trường cần thiết
        const templateId = template.id || template.key || `template_${Math.random().toString(36).substring(2, 9)}`;
        
        // Đảm bảo config được xử lý đúng dạng
        let config = {};
        if (template.config) {
          try {
            config = typeof template.config === 'string' ? JSON.parse(template.config) : template.config;
          } catch (err) {
            console.warn(`Failed to parse config for template ${templateId}:`, err);
          }
        }
        
        return {
          ...template,
          id: templateId,
          type: template.type || 'http',  // Mặc định là http
          key: templateId, // Đảm bảo có key dùng làm identifier
          config: config, // Đảm bảo config luôn là object
          name: template.name || 'Unnamed Template',
          description: template.description || 'No description'
        };
      });
      
      console.log('Processed templates:', processedTemplates);
      return { templates: processedTemplates };
    } catch (error) {
      console.error('Error fetching templates:', error);
      return { templates: [] };
    }
  }
  
  // Removed getTemplate() method - not implemented in backend
  
  // Agent Mappings
  async getAgentMappings(agentName) {
    const response = await apiClient.get(`/api/v1/master/mcp/agents/${agentName}/mappings`);
    return response.data;
  }
  
  async addAgentMapping(agentName, serverId, data) {
    try {
      console.log(`Gọi API gán agent: agentName=${agentName}, serverId=${serverId}`, data);
      
      // Thêm agent_name và mcp_server_id vào body request để tương thích với schema backend
      const requestData = {
        ...data,
        agent_name: agentName,
        mcp_server_id: serverId
      };
      
      console.log(`Body request gửi đi:`, requestData);
      
      const response = await apiClient.post(
        `/api/v1/master/mcp/agents/${agentName}/servers/${serverId}`,
        requestData
      );
      
      console.log(`API gán agent trả về:`, response.data);
      return response.data;
    } catch (error) {
      console.error(`Lỗi API gán agent ${agentName}:`, error.response?.data || error.message);
      if (error.response?.status === 400 && error.response?.data?.detail) {
        console.error(`Chi tiết lỗi:`, error.response.data.detail);
      }
      if (error.response?.status === 422 && error.response?.data?.detail) {
        console.error(`Lỗi validation:`, error.response.data.detail);
      }
      throw error; // Rethrow để component xử lý
    }
  }
  
  async updateAgentMapping(agentName, serverId, data) {
    const response = await apiClient.put(
      `/api/v1/master/mcp/agents/${agentName}/servers/${serverId}`,
      data
    );
    return response.data;
  }
  
  async removeAgentMapping(agentName, serverId) {
    const response = await apiClient.delete(
      `/api/v1/master/mcp/agents/${agentName}/servers/${serverId}`
    );
    return response.data;
  }
}

export default new MCPMasterService();

